import { useMemo } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import { Typography } from "@mui/material";
import { bucketByRank, directionColorBucket, parseLatLng } from "./cdrEngine";

export function buildMapData(filteredAnalysis, filteredNormRows, topN) {
  const mflEntries = (filteredAnalysis?.mflAll || []).slice(0, topN);
  if (!mflEntries.length) return { markers: [], unresolvedCount: 0, isEmpty: true };

  const coordsByLocation = new Map();
  for (const r of filteredNormRows || []) {
    const loc = r.location || r.siteLocation || "(blank)";
    if (coordsByLocation.has(loc)) continue;
    const coords = parseLatLng(r);
    if (coords) coordsByLocation.set(loc, coords);
  }

  const markers = [];
  let unresolvedCount = 0;
  mflEntries.forEach((entry, i) => {
    const coords = coordsByLocation.get(entry.location);
    if (!coords) { unresolvedCount += 1; return; }
    markers.push({
      location: entry.location,
      lat: coords.lat,
      lng: coords.lng,
      inCall: entry.inCall, outCall: entry.outCall, inSms: entry.inSms, outSms: entry.outSms,
      total: entry.total,
      sizeBucket: bucketByRank(i, mflEntries.length),
      colorBucket: directionColorBucket(entry),
    });
  });

  return { markers, unresolvedCount, isEmpty: markers.length === 0 };
}

const SIZE_PX = { large: 28, medium: 22, small: 16 };
const COLOR_BY_BUCKET = { in: "#16a34a", out: "#ea580c", balanced: "#6b7280" };

function createTowerIcon(sizeBucket, colorBucket) {
  const px = SIZE_PX[sizeBucket] || 22;
  const color = COLOR_BY_BUCKET[colorBucket] || "#6b7280";
  const html = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${px}" height="${px}">
      <line x1="12" y1="2" x2="12" y2="22" stroke="${color}" stroke-width="2" stroke-linecap="round" />
      <line x1="5" y1="9" x2="19" y2="6" stroke="${color}" stroke-width="2" stroke-linecap="round" />
      <line x1="5" y1="15" x2="19" y2="12" stroke="${color}" stroke-width="2" stroke-linecap="round" />
      <circle cx="12" cy="2" r="2.4" fill="${color}" />
    </svg>`;
  return L.divIcon({ html, className: "cdr-tower-divicon", iconSize: [px, px], iconAnchor: [px / 2, px] });
}

export default function CDRMap({ filteredAnalysis, filteredNormRows, topN }) {
  const data = useMemo(
    () => buildMapData(filteredAnalysis, filteredNormRows, topN),
    [filteredAnalysis, filteredNormRows, topN]
  );

  const bounds = useMemo(() => {
    if (!data.markers.length) return null;
    return L.latLngBounds(data.markers.map((m) => [m.lat, m.lng]));
  }, [data.markers]);

  if (data.isEmpty) {
    return (
      <Typography variant="body2" color="text.secondary">
        No locations to map (check filters).
      </Typography>
    );
  }

  return (
    <div>
      {data.unresolvedCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {data.unresolvedCount} location(s) in this list have no plottable coordinates and are not shown on the map.
        </Typography>
      )}
      <div style={{ height: 520, width: "100%", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
        <MapContainer bounds={bounds} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MarkerClusterGroup>
            {data.markers.map((m) => (
              <Marker key={m.location} position={[m.lat, m.lng]} icon={createTowerIcon(m.sizeBucket, m.colorBucket)}>
                <Popup>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{m.location}</div>
                  <div>Incoming Call: {m.inCall}</div>
                  <div>Outgoing Call: {m.outCall}</div>
                  <div>Incoming SMS: {m.inSms}</div>
                  <div>Outgoing SMS: {m.outSms}</div>
                  <div style={{ fontWeight: 700, marginTop: 4 }}>Total: {m.total}</div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </div>
  );
}
